import { memo, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BATCH_PERSON_CSV_TEMPLATE } from "./batchPersonFileAccept";
import { usePersonBatchRegister } from "./usePersonBatchRegister";
import PersonBatchRegisterSummary from "./PersonBatchRegisterSummary";
import PersonBatchRegisterFileExample from "./PersonBatchRegisterFileExample";

type PersonBatchRegisterDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationId?: string;
};

function downloadTemplate() {
  const blob = new Blob([BATCH_PERSON_CSV_TEMPLATE], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "modelo-cadastro-pessoas.csv";
  link.click();
  URL.revokeObjectURL(url);
}

const PersonBatchRegisterDialog = memo(
  ({ open, onClose, organizationId }: PersonBatchRegisterDialogProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const register = usePersonBatchRegister(organizationId);
    const progressPercent =
      register.progressTotal === 0
        ? 0
        : Math.round((register.progressDone / register.progressTotal) * 100);

    const handleClose = () => {
      if (register.isRegistering) return;
      register.reset();
      onClose();
    };

    return (
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={register.isRegistering}
        onClose={(_event, reason) => {
          if (
            register.isRegistering &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          handleClose();
        }}
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
          Cadastrar pessoas
          {!register.isRegistering && (
            <IconButton
              aria-label="Fechar"
              onClick={handleClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {register.step === "upload" && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Envie uma planilha CSV ou um arquivo JSON. Confira o modelo
                  abaixo antes de escolher o arquivo.
                </Typography>
                <PersonBatchRegisterFileExample />
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.json,text/csv,application/json"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    void register.handleFile(file);
                    event.target.value = "";
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => inputRef.current?.click()}
                  sx={{ textTransform: "none", fontWeight: 600, py: 1.25 }}
                >
                  Escolher arquivo
                </Button>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  sx={{ textTransform: "none", alignSelf: "flex-start" }}
                >
                  Baixar modelo
                </Button>
                {register.fileError && (
                  <Alert severity="error">{register.fileError}</Alert>
                )}
              </>
            )}

            {register.step === "summary" && (
              <PersonBatchRegisterSummary
                fileName={register.fileName}
                isChecking={register.isChecking}
                summary={register.summary}
              />
            )}

            {register.step === "progress" && (
              <Stack spacing={2}>
                <Alert severity="warning">
                  Não feche, não atualize e não troque de tela até terminar.
                </Alert>
                <Typography variant="body2">
                  Cadastrando {register.progressDone} de {register.progressTotal}
                  …
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{ height: 10, borderRadius: 999 }}
                />
              </Stack>
            )}

            {register.step === "result" && (
              <Stack spacing={1.5}>
                <Alert
                  severity={register.failures.length === 0 ? "success" : "warning"}
                >
                  {register.failures.length === 0
                    ? `${register.successCount} ${register.successCount === 1 ? "pessoa cadastrada" : "pessoas cadastradas"} com sucesso.`
                    : `${register.successCount} cadastradas. ${register.failures.length} não puderam ser cadastradas.`}
                </Alert>
                {register.failures.slice(0, 8).map((item) => (
                  <Typography key={item.row.lineNumber} variant="body2">
                    Linha {item.row.lineNumber} — {item.row.name}
                  </Typography>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {register.step === "summary" && (
            <>
              <Button
                onClick={() => register.reset()}
                disabled={register.isChecking}
                sx={{ textTransform: "none" }}
              >
                Trocar arquivo
              </Button>
              <Button
                variant="contained"
                disabled={
                  register.isChecking || !register.summary?.toRegister.length
                }
                onClick={() => void register.startRegister()}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cadastrar pessoas
              </Button>
            </>
          )}
          {register.step === "result" && register.failures.length > 0 && (
            <Button
              onClick={() => void register.retryFailures()}
              sx={{ textTransform: "none" }}
            >
              Tentar novamente
            </Button>
          )}
          {register.step === "result" && (
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Concluir
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

PersonBatchRegisterDialog.displayName = "PersonBatchRegisterDialog";
export default PersonBatchRegisterDialog;
